import { useEffect, useRef } from "react";
import { io } from "socket.io-client";
import { showToast } from "../../../utils/toast";
import Swal from 'sweetalert2';
export default function useSocketListener({
  currentLanguage,
  setNotifications,
  setHasNewRequest,
  setShowNotification,
  currentUser
}) {
  const socketRef = useRef(null);

  useEffect(() => {
    if (!socketRef.current) {
      socketRef.current = io("https://onepasscms-backend-tvdy.onrender.com", {
        transports: ["websocket", "polling"],
        reconnection: true,
        reconnectionAttempts: Infinity,
        reconnectionDelay: 1000,
        reconnectionDelayMax: 5000,
        timeout: 20000,
      });
    }

    const socket = socketRef.current;
    const handleForceLogout = (msg) => {
        console.warn("⚠️ Nhận tín hiệu force_logout:", msg);

        // Nội dung thông báo (Việt là chính, Anh/Hàn là phụ)
        const contentHtml = `
          <div style="margin-bottom: 15px; line-height: 1.4;">
            <small style="color: #555;">
              <i>Session Expired</i><br/>
              <i>세션이 만료되었습니다</i>
            </small>
          </div>
          <hr style="margin: 10px 0; border: 0; border-top: 1px solid #eee;" />
          <div style="font-size: 1.1em; font-weight: 500; color: #222; margin-bottom: 8px;">
            ${msg || "Tài khoản của bạn đã được đăng nhập ở thiết bị khác."}
          </div>
          <div style="line-height: 1.4;">
            <small style="color: #666;">
              <i>Your account has been logged in on another device.</i><br/>
              <i>귀하의 계정이 다른 기기에서 로그인되었습니다.</i>
            </small>
          </div>
        `;

        Swal.fire({
          icon: 'warning',
          title: 'Phiên đăng nhập hết hạn',
          html: contentHtml,
          allowOutsideClick: false,
          allowEscapeKey: false,
          showCancelButton: false,
          confirmButtonColor: '#d33',
        
          confirmButtonText: 'Đóng & Đăng xuất', 
          
         
          didOpen: () => {
            // Lấy phần tử nút bấm
            const confirmBtn = Swal.getConfirmButton();
            
        
            if (confirmBtn) {
              confirmBtn.style.lineHeight = "1.2"; // Giãn dòng để không bị dính
              confirmBtn.innerHTML = `
                <div style="display: flex; flex-direction: column; align-items: center;">
                  <span style="font-size: 16px; font-weight: 600;">Đóng & Đăng xuất</span>
                  <span style="font-size: 11px; font-style: italic; margin-top: 4px; opacity: 0.9;">
                    Close & Logout
                  </span>
                     <span style="font-size: 11px; font-style: italic; margin-top: 4px; opacity: 0.9;">
                        닫기 및 로그아웃
                  </span>
                </div>
              `;
            }
          }
          // -------------------------------------

        }).then((result) => {
          if (result.isConfirmed) {
            localStorage.removeItem("currentUser");
            localStorage.removeItem("sessionToken");
            window.location.href = "/login";
          }
        });
      };

      socket.on("force_logout", handleForceLogout);
      const handleConnect = () => {
        if (currentUser?.id) {
          socket.emit("register_user", currentUser.id);
       }
    };
    
    const handleDisconnect = (reason) =>
      console.log("🔴 Socket disconnected. Reason:", reason);
    const handleError = (error) => console.error("❌ Socket error:", error);
    const handleReconnectAttempt = (attempt) =>
      console.log(`🔄 Reconnect attempt #${attempt}...`);
    const handleReconnect = (attempt) =>
      console.log(`✅ Reconnected successfully after ${attempt} attempts.`);

    socket.on("connect", handleConnect);
    socket.on("disconnect", handleDisconnect);
    socket.on("connect_error", handleError);
    socket.on("reconnect_attempt", handleReconnectAttempt);
    socket.on("reconnect", handleReconnect);

    // =====================================
    // 🎯 1) EVENT CŨ: "new_request"
    // =====================================
    const handleNewRequest = (newRequestData) => {
      const message =
        currentLanguage === "vi"
          ? `Yêu cầu mới từ: ${newRequestData.HoTen || "Khách hàng"}`
          : `New request from: ${newRequestData.HoTen || "Customer"}`;

      const newNotification = {
        id: Date.now(),
        message,
        time: new Date().toLocaleTimeString("vi-VN"),
        requestId: newRequestData.YeuCauID,
      };

      setNotifications((prev) => {
        const updated = [newNotification, ...prev.slice(0, 9)];
        localStorage.setItem("notifications", JSON.stringify(updated));
        return updated;
      });

      showToast(message, "success");

      if ("Notification" in window && Notification.permission === "granted") {
        new Notification("📩 " + message, {
          body:
            currentLanguage === "vi"
              ? "Có yêu cầu mới trong hệ thống CMS"
              : "A new request has arrived in CMS",
          icon: "/favicon_logo.png",
        });
      }

      setHasNewRequest(true);
      setShowNotification(true);
    };

    socket.on("new_request", handleNewRequest);

    // =====================================
    // 🎯 2) EVENT MỚI: ADMIN ĐĂNG KÝ SERVICE
    // =====================================
    const handleNewB2BService = (data) => {
      const msg =
        currentLanguage === "vi"
          ? `ADMIN đăng ký dịch vụ mới: ${data.tenDichVu || ""}`
          : `Admin created new B2B service`;

      const newNotification = {
        id: Date.now(),
        message: msg,
        time: new Date().toLocaleTimeString("vi-VN"),
        serviceId: data.serviceId,
      };

      setNotifications((prev) => {
        const updated = [newNotification, ...prev.slice(0, 9)];
        localStorage.setItem("notifications", JSON.stringify(updated));
        return updated;
      });

      showToast(msg, "info");
      setHasNewRequest(true);
      setShowNotification(true);
    };

    socket.on("b2b_new_service", handleNewB2BService);

    // =====================================
    // 🎯 3) EVENT MỚI: SERVICE ĐƯỢC DUYỆT
    // =====================================
    const handleApprovedService = (data) => {
      const msg =
        currentLanguage === "vi"
          ? `Dịch vụ '${data.tenDichVu}' đã được duyệt`
          : `Your assigned service has been approved`;

      const newNotification = {
        id: Date.now(),
        message: msg,
        time: new Date().toLocaleTimeString("vi-VN"),
        serviceId: data.serviceId,
      };

      setNotifications((prev) => {
        const updated = [newNotification, ...prev.slice(0, 9)];
        localStorage.setItem("notifications", JSON.stringify(updated));
        return updated;
      });

      showToast(msg, "success");
      setHasNewRequest(true);
      setShowNotification(true);
    };

    socket.on("b2b_service_approved", handleApprovedService);

    // CLEANUP
    return () => {
      socket.off("new_request", handleNewRequest);
      socket.off("b2b_new_service", handleNewB2BService);
      socket.off("b2b_service_approved", handleApprovedService);

      socket.off("connect", handleConnect);
      socket.off("force_logout", handleForceLogout);
      socket.off("disconnect", handleDisconnect);
      socket.off("connect_error", handleError);
      socket.off("reconnect_attempt", handleReconnectAttempt);
      socket.off("reconnect", handleReconnect);
    };
  }, [currentLanguage, setNotifications, setHasNewRequest, setShowNotification, currentUser]);
}
